import { useCallback } from 'react'
import { useMutation, useQueryClient } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { APIError } from '../../../../../utils'
import { deleteFecImport } from '../query-api'
import { isFecImportNotFoundError } from '../utils/isFecImportNotFoundError'

interface UseDeleteFecImportProps {
  onError?: (error?: APIError) => void
  onSuccess?: () => void
}

export class PartialDeleteError extends Error {
  public deletedIds: string[]
  public failedIds: string[]

  public constructor(deletedIds: string[], failedIds: string[]) {
    super(failedIds.join(', '))
    this.name = 'PartialDeleteError'
    this.deletedIds = deletedIds
    this.failedIds = failedIds
  }
}

export const isPartialDeleteError = (error: unknown): error is PartialDeleteError => error instanceof PartialDeleteError

export const useDeleteFecImport = (props?: UseDeleteFecImportProps) => {
  const { onError, onSuccess } = props || {}
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()

  // Returned, so `deleteMany` resolves once the list no longer holds the deleted imports.
  const invalidateFecImports = useCallback(
    () => queryClient.invalidateQueries([QueryKeys.FecImports, organization?.id]),
    [organization?.id, queryClient],
  )

  const {
    mutate,
    isLoading: isProcessing,
    ...rest
  } = useMutation((fecImportId: string) => deleteFecImport(organization?.id || '', fecImportId), {
    onError,
    onSuccess: () => {
      invalidateFecImports()
      onSuccess?.()
    },
  })

  const deleteMany = useCallback(
    async (fecImportIds: string[]) => {
      const organizationId = organization?.id || ''
      const results = await Promise.allSettled(
        fecImportIds.map((fecImportId) => deleteFecImport(organizationId, fecImportId)),
      )

      const deletedIds: string[] = []
      const failedIds: string[] = []

      for (const [index, fecImportId] of fecImportIds.entries()) {
        const result = results[index]

        if (
          result?.status === 'fulfilled' ||
          isFecImportNotFoundError(result?.status === 'rejected' ? result.reason : undefined)
        ) {
          deletedIds.push(fecImportId)
        } else {
          failedIds.push(fecImportId)
        }
      }

      await invalidateFecImports()

      if (failedIds.length > 0) {
        throw new PartialDeleteError(deletedIds, failedIds)
      }
    },
    [invalidateFecImports, organization?.id],
  )

  return { delete: mutate, deleteMany, isProcessing, ...rest }
}
